// The platform's published price list, for the figures the app prints in copy
// (add-on cards, package descriptions, the invite wizard). Mirrors
// backend/src/lib/pricing.ts, which is the copy that bills; the marketing site
// carries the same numbers in website/src/pages/BuildPrice.tsx (PRICES).
// Change a price in all three.

export const CE_FLAT_PENCE = 4_900;
export const CEP_FLAT_PENCE = 14_900;
export const STANDARD_FIRST_PENCE = 24_900;
export const STANDARD_SECOND_PENCE = 14_900;
export const STANDARD_ADDITIONAL_PENCE = 9_900;

/** Tier add-on per level; L4 and L5 cost the same as L3 (consultant and bureau are pass-throughs). */
export const TIER_ADDON_PENCE: Record<number, number> = { 1: 0, 2: 3_500, 3: 6_900, 4: 6_900, 5: 6_900 };
export const TIER_AI_CREDIT_PENCE = 2_000;

export const TPRM_ADDON_PENCE = 24_900;
export const DORA_MONITORING_ADDON_PENCE = 24_900;
export const ESG_ADDON_PENCE = 9_900;
/** Auditor access below Full Service (backend lib/auditorAccess.ts); Full Service includes it. */
export const AUDITOR_ACCESS_ADDON_PENCE = 1_500;
export const JURISDICTION_ADDON_PENCE = 4_900;
export const HARDENING_BENCHMARK_PENCE = 1_000;
/** PCI DSS SAQ types: the first is inside the standard fee, the second adds £19/mo, each further one £9/mo
 *  (backend lib/pricing.ts pciSaqFee; website BuildPrice.tsx PRICES.saqSecond / saqAdditional). */
export const PCI_SAQ_SECOND_PENCE = 1_900;
export const PCI_SAQ_ADDITIONAL_PENCE = 900;
export function pciSaqFee(typeCount: number): number {
  const n = Math.max(0, Math.floor(typeCount));
  return n <= 1 ? 0 : PCI_SAQ_SECOND_PENCE + (n - 2) * PCI_SAQ_ADDITIONAL_PENCE;
}
export const INTEGRATION_ADDON_PENCE = 2_500;

// CE Small Business Annual Package
export const CE_ANNUAL_PENCE = 60_000;
export const CE_SB_TIER_ADDON_PENCE = 2_500;
export const CE_SB_AI_CREDIT_PENCE = 1_000;
export const JURISDICTION_ANNUAL_PENCE = 24_900;

// Jersey edition package (backend lib/pricing.ts JERSEY_*): £1,350 a year over a three-year term,
// billed monthly or annually (the client's choice), the £450 certificate and its surveillance inside
// it, 7 / 2 / 2 consultancy hours, £5/mo AI credit; CE+ £1,000 a year on top. A licensed consultancy
// pays JERSEY_SEAT_LICENCE_PENCE per consultant seat instead.
export const JERSEY_PACKAGE_ANNUAL_PENCE = 135_000;
export const JERSEY_PACKAGE_MONTHLY_PENCE = 11_250;
export const JERSEY_PACKAGE_TERM_MONTHS = 36;
export const JERSEY_CERTIFICATE_ANNUAL_PENCE = 45_000;
export const JERSEY_CEP_ANNUAL_PENCE = 100_000;
export const JERSEY_CONSULTANCY_HOURS_BY_YEAR = [7, 2, 2] as const;
export const JERSEY_PACKAGE_AI_CREDIT_PENCE = 500;
export const JERSEY_SEAT_LICENCE_PENCE = 1_000;

// Segment bundles
export const BUNDLE_FLAT_RATE_PENCE: Record<string, number> = { small: 24_900, medium: 49_900, large: 99_900 };
export const ENTERPRISE_FLOOR_PENCE = 300_000;

// ── Enterprise scaling: people in scope and managed endpoints ────────────
// Mirrors the tables in backend/src/lib/pricing.ts; the API's breakdown carries
// the billed lines, these only explain them in copy. Logins stay unlimited.

export interface MarginalBand { upTo: number; pence: number }

/** Marginal (income-tax style) fee: units up to `included` are free, then each band's rate applies inside it. */
export function marginalBandFee(count: number, included: number, bands: readonly MarginalBand[]): number {
  let fee = 0;
  let lower = included;
  for (const band of bands) {
    if (count <= lower) break;
    fee += (Math.min(count, band.upTo) - lower) * band.pence;
    lower = band.upTo;
  }
  return fee;
}

export const PEOPLE_LIST_PRICE_MAX = 150;
export const MEDIUM_BUNDLE_PER_PERSON_PENCE = 150;   // £1.50 per person above 150 — Medium at 200 = £574
export const MEDIUM_BUNDLE_PEOPLE_MAX = 200;
export const PAYG_VOLUME_STANDARDS_MULTIPLIER = 1.5;  // pay-as-you-go 151–500: standards × 1.5
export const PAYG_PEOPLE_MAX = 500;                   // pay-as-you-go is not offered above this
export const ENTERPRISE_FLOOR_PEOPLE = 1_000;
export const ENTERPRISE_PEOPLE_BANDS: readonly MarginalBand[] = [
  { upTo: 2_500, pence: 120 }, { upTo: 5_000, pence: 80 }, { upTo: 10_000, pence: 50 }, { upTo: Infinity, pence: 30 },
];
export const ENTERPRISE_AI_CREDIT_PENCE = 5_000;         // at the floor
export const ENTERPRISE_AI_CREDIT_STEP_PENCE = 2_500;
export const ENTERPRISE_AI_CREDIT_STEP_PEOPLE = 1_000;
export const ENDPOINTS_INCLUDED = 250;
export const ENDPOINT_BANDS: readonly MarginalBand[] = [
  { upTo: 1_000, pence: 40 }, { upTo: 5_000, pence: 25 }, { upTo: 20_000, pence: 15 }, { upTo: Infinity, pence: 10 },
];
export const SCALING_CEILING_PENCE = 1_500_000;      // £15,000/mo before pass-through; above it a dedicated-instance quote
export const SCALING_CONTRACT_NOTES = { commitmentPeople: 1_000, commitmentMonths: 12, prepayDiscountPercent: 10 } as const;

export type PeopleScalingRule = "list" | "medium_per_person" | "payg_standards_x1_5" | "enterprise_bands";
export type PeopleScalingModel = "small_business" | "payg" | "small" | "medium" | "large" | "enterprise";

export function enterprisePeopleFee(headcount: number): number {
  return marginalBandFee(headcount, ENTERPRISE_FLOOR_PEOPLE, ENTERPRISE_PEOPLE_BANDS);
}

export function enterpriseAiCreditPence(headcount: number | null): number {
  const above = Math.max(0, (headcount ?? 0) - ENTERPRISE_FLOOR_PEOPLE);
  return ENTERPRISE_AI_CREDIT_PENCE + Math.floor(above / ENTERPRISE_AI_CREDIT_STEP_PEOPLE) * ENTERPRISE_AI_CREDIT_STEP_PENCE;
}

/** What the people in scope add on a billing model (pence) and the rule behind it — the backend's peopleScalingFee. */
export function peopleScalingFee(headcount: number | null, model: PeopleScalingModel, standardsFeePence = 0): { pence: number; rule: PeopleScalingRule } {
  const people = headcount ?? 0;
  if (model === "enterprise") {
    const pence = enterprisePeopleFee(people);
    return { pence, rule: pence > 0 ? "enterprise_bands" : "list" };
  }
  if (people <= PEOPLE_LIST_PRICE_MAX) return { pence: 0, rule: "list" };
  if (model === "medium") return { pence: (people - PEOPLE_LIST_PRICE_MAX) * MEDIUM_BUNDLE_PER_PERSON_PENCE, rule: "medium_per_person" };
  if (model === "payg") return { pence: Math.round(standardsFeePence * (PAYG_VOLUME_STANDARDS_MULTIPLIER - 1)), rule: "payg_standards_x1_5" };
  return { pence: 0, rule: "list" };
}

/** What the managed endpoints add (pence): the marginal bands above the 250 every plan includes. */
export function endpointScalingFee(count: number): number {
  return marginalBandFee(Math.max(0, Math.floor(count)), ENDPOINTS_INCLUDED, ENDPOINT_BANDS);
}

/** "£1.20" — a per-unit rate in pounds and pence. */
export const gbpRate = (pence: number) => `£${(pence / 100).toFixed(2)}`;

/** "People in scope — 1,500 above 1,000 on the Enterprise bands" — the label for a people-scaling line. */
export function peopleScalingLabel(rule: PeopleScalingRule, headcount: number | null): string {
  const n = (headcount ?? 0).toLocaleString("en-GB");
  switch (rule) {
    case "medium_per_person":
      return `People in scope — ${n}, ${gbpRate(MEDIUM_BUNDLE_PER_PERSON_PENCE)} per person above ${PEOPLE_LIST_PRICE_MAX}`;
    case "payg_standards_x1_5":
      return `People in scope — ${n}, standards × ${PAYG_VOLUME_STANDARDS_MULTIPLIER}`;
    case "enterprise_bands":
      return `People in scope — ${n}, banded above ${ENTERPRISE_FLOOR_PEOPLE.toLocaleString("en-GB")}`;
    default:
      return `People in scope — ${n}`;
  }
}

/** "Managed endpoints — 1,200 (250 included)". */
export function endpointScalingLabel(count: number): string {
  return `Managed endpoints — ${count.toLocaleString("en-GB")} (${ENDPOINTS_INCLUDED} included)`;
}

/** "£249" / "£3,000" — whole pounds, for copy. */
export function gbp(pence: number): string {
  return `£${Math.round(pence / 100).toLocaleString("en-GB")}`;
}

/** "£249/mo". */
export const gbpMo = (pence: number) => `${gbp(pence)}/mo`;

/**
 * What each package includes, as the consultant and the client see it.
 * Mirrors PACKAGES in backend/src/lib/bundles.ts, which is what the API
 * enforces; this copy only drives labels and the invite wizard's pre-selection.
 */
export interface PackageInfo {
  id: "ce_annual" | "small" | "medium" | "large" | "enterprise";
  name: string;
  /** Text for the picker option. */
  option: string;
  /** One-line summary shown under the picker. */
  summary: string;
  tierLevel: 4 | 5;
  tierFixed: boolean;
  includedFrameworkIds: string[];
  addons: { tprm: boolean; dora: boolean; esg: boolean };
}

export const PACKAGES: PackageInfo[] = [
  {
    id: "ce_annual",
    name: "CE Annual Package (micro)",
    option: `CE Annual Package — Micro (${gbp(CE_ANNUAL_PENCE + 12 * CE_SB_TIER_ADDON_PENCE)}/yr · up to 10 employees)`,
    summary: `${gbp(CE_ANNUAL_PENCE)}/yr CE framework (billed at ${gbp(CE_ANNUAL_PENCE / 12)}/mo) · ${gbp(CE_SB_TIER_ADDON_PENCE)}/mo Consultant-Led tier · includes ${gbp(CE_SB_AI_CREDIT_PENCE)}/mo AI credit ≈ ${gbp(CE_ANNUAL_PENCE / 12 + CE_SB_TIER_ADDON_PENCE)}/mo base. Cyber Essentials only; one jurisdiction at ${gbp(JURISDICTION_ANNUAL_PENCE)}/yr. The client is put on Consultant-Led automatically; clear the package when they are ready to grow.`,
    tierLevel: 4,
    tierFixed: true,
    includedFrameworkIds: ["cyber_essentials"],
    addons: { tprm: false, dora: false, esg: false },
  },
  {
    id: "small",
    name: "Small Business Bundle",
    option: `Small Business Bundle (${gbpMo(BUNDLE_FLAT_RATE_PENCE.small)} · CE + ISO 27001 + L4)`,
    summary: "Includes CE + ISO 27001, the Consultant-Led tier, 2 jurisdictions and a £10/mo AI credit — applied automatically. Further standards, jurisdictions and add-ons bill at standard rates.",
    tierLevel: 4,
    tierFixed: false,
    includedFrameworkIds: ["cyber_essentials", "iso27001"],
    addons: { tprm: false, dora: false, esg: false },
  },
  {
    id: "medium",
    name: "Medium Business Bundle",
    option: `Medium Business Bundle (${gbpMo(BUNDLE_FLAT_RATE_PENCE.medium)} · 3 standards + TPRM)`,
    summary: `Includes CE + ISO 27001 + ISO 9001, the Consultant-Led tier, TPRM, 3 jurisdictions and a £20/mo AI credit — applied automatically. Priced to ${PEOPLE_LIST_PRICE_MAX} people; ${gbpRate(MEDIUM_BUNDLE_PER_PERSON_PENCE)} per person above that. DORA, ESG, CIS hardening and further standards bill at standard rates.`,
    tierLevel: 4,
    tierFixed: false,
    includedFrameworkIds: ["cyber_essentials", "iso27001", "iso9001"],
    addons: { tprm: true, dora: false, esg: false },
  },
  {
    id: "large",
    name: "Large Business Bundle",
    option: `Large Business Bundle (${gbpMo(BUNDLE_FLAT_RATE_PENCE.large)} · 4 standards + TPRM + DORA)`,
    summary: "Includes CE + ISO 27001 + ISO 9001 + SOC 2, the Full Service tier, TPRM + DORA, 5 jurisdictions and a £35/mo AI credit — applied automatically. ESG, CIS hardening and further standards bill at standard rates.",
    tierLevel: 5,
    tierFixed: false,
    includedFrameworkIds: ["cyber_essentials", "iso27001", "iso9001", "soc2"],
    addons: { tprm: true, dora: true, esg: false },
  },
  {
    id: "enterprise",
    name: "Enterprise Bundle",
    option: `Enterprise Bundle (${gbpMo(ENTERPRISE_FLOOR_PENCE)} min · full suite)`,
    summary: `All add-ons included, unlimited jurisdictions, the Full Service tier and a £50/mo AI credit — applied automatically. Platform fee = max(itemised, ${gbpMo(ENTERPRISE_FLOOR_PENCE)}) for up to ${ENTERPRISE_FLOOR_PEOPLE.toLocaleString("en-GB")} people, then per-person bands from ${gbpRate(ENTERPRISE_PEOPLE_BANDS[0].pence)}; the AI credit rises ${gbp(ENTERPRISE_AI_CREDIT_STEP_PENCE)}/mo per further 1,000 people. Bureau pass-through still billed on top.`,
    tierLevel: 5,
    tierFixed: false,
    includedFrameworkIds: [],
    addons: { tprm: true, dora: true, esg: true },
  },
];

// ── Consultancy allocation (display mirror of backend/src/lib/consultancyAllocation.ts) ─────────────

/** Consultancy time belongs to Consultant-Led (L4) and above; below it an allocation bills nothing. */
export const ALLOCATION_MIN_TIER = 4;

/**
 * What an allocation of days and hours a month bills, for a preview of values not yet saved. The saved
 * figure always comes from the API (`billingBreakdown.consultancyPence`); this only re-prices what the
 * consultant is typing.
 */
export function consultancyAllocationPence(o: { tierLevel: number; days: number; hours: number; dayRatePence: number; hourRatePence: number }): number {
  if (o.tierLevel < ALLOCATION_MIN_TIER) return 0;
  return Math.max(0, Math.round(o.days * o.dayRatePence + o.hours * o.hourRatePence));
}

/** "2 days and 6 hours" / "3 days" / "4 hours": an allocation in words, for the confirm step. */
export function describeAllocationMonthly(days: number, hours: number): string {
  const parts = [
    days > 0 ? `${days} day${days === 1 ? "" : "s"}` : null,
    hours > 0 ? `${hours} hour${hours === 1 ? "" : "s"}` : null,
  ].filter((x): x is string => !!x);
  return `${parts.join(" and ")} per month`;
}

/**
 * The question to ask before a move to a tier below Consultant-Led, or null when the move does not end an
 * allocation. The server refuses the move without the confirmation (confirmClearAllocation).
 */
export function allocationClearPrompt(o: { consultancyDaysPerMonth: number; consultancyHoursRemaining: number }, newTierLevel: number): string | null {
  if (newTierLevel >= ALLOCATION_MIN_TIER) return null;
  if (!(o.consultancyDaysPerMonth > 0 || o.consultancyHoursRemaining > 0)) return null;
  return `This ends the ${describeAllocationMonthly(o.consultancyDaysPerMonth, o.consultancyHoursRemaining)} consultancy allocation. Consultancy time starts at Consultant-Led; moving back up starts from zero. Continue?`;
}

/** One line saying how delivered time compares with the allocation, or null when they agree. */
export function deliveryDifferenceNote(flag: "none" | "below" | "above" | null | undefined, differencePence: number | null | undefined, fmt: (pence: number) => string): string | null {
  if (!flag || flag === "none" || differencePence == null) return null;
  return flag === "below"
    ? `Delivered is ${fmt(Math.abs(differencePence))} below the allocation`
    : `Delivered is ${fmt(Math.abs(differencePence))} above the allocation`;
}

export const packageById = (id: string | null | undefined): PackageInfo | undefined =>
  PACKAGES.find((p) => p.id === id);

/** The package an organisation is on, from its two billing flags (the CE package wins, as billing does). */
export function packageOf(org: { smallBusinessPackage?: boolean | null; bundleTier?: string | null }): PackageInfo | undefined {
  if (org.smallBusinessPackage) return packageById("ce_annual");
  return packageById(org.bundleTier);
}
